import { SystemError } from '../../../errors';
import type { Boost } from '../descriptor';
import type { ElemPredicate, NumOp, Predicate, Scalar } from '../predicate';

const NEVER_FILTER = '_tags:__search_never__ AND NOT _tags:__search_never__';
// The sentinel's bare AND binds at the enclosing precedence level; parenthesised form is required inside a group.
const GROUPED_NEVER_FILTER = '(' + NEVER_FILTER + ')';
const RESERVED_KEYWORD = /^(AND|OR|NOT)$/i;
const SAFE_FIELD = /^[A-Za-z_][A-Za-z0-9_.]*$/;
const SAFE_STRING_VALUE = /^[A-Za-z0-9_.-]+$/;

const quote = (value: string): string => JSON.stringify(value);

const field = (value: string): string =>
  SAFE_FIELD.test(value) && !RESERVED_KEYWORD.test(value) ? value : quote(value);

const finiteNumber = (value: number): string => {
  if (!Number.isFinite(value)) {
    throw new SystemError({
      code: 'SEARCH_FILTER_NON_FINITE_NUMBER',
      userMessage: 'Algolia filters require a finite number; received ' + String(value) + '.',
      retryable: false,
    });
  }

  return String(value);
};

const scalar = (value: Scalar): string => {
  if (typeof value === 'number') return finiteNumber(value);
  if (typeof value === 'boolean') return String(value);

  return SAFE_STRING_VALUE.test(value) && !RESERVED_KEYWORD.test(value) ? value : quote(value);
};

/** Grammar rules: stated once here, enforced by `validate`; `render` runs on an already-validated tree. */

/** A parenthesised group never nests inside another parenthesised group. */
function assertNoGroupInsideOr<T>(predicate: Predicate<T>): void {
  if (predicate.kind !== 'and' && predicate.kind !== 'or' && predicate.kind !== 'in') return;

  throw new SystemError({
    code: 'SEARCH_FILTER_UNSUPPORTED_GROUP_IN_OR',
    userMessage: 'Algolia filters do not support an ' + predicate.kind.toUpperCase() + ' group inside OR.',
    retryable: false,
  });
}

function assertNonEmptyInList<T>(predicate: Extract<Predicate<T>, { kind: 'in' }>): void {
  if (predicate.values.length === 0) {
    throw new SystemError({
      code: 'SEARCH_FILTER_EMPTY_IN_LIST',
      userMessage: 'Algolia filters cannot compile an empty IN list.',
      retryable: false,
    });
  }
}

function assertNegatable<T>(
  operand: Predicate<T>
): asserts operand is Extract<Predicate<T>, { kind: 'eq' | 'eqStringLiteral' }> {
  if (operand.kind === 'eq' || operand.kind === 'eqStringLiteral') return;

  if (operand.kind === 'and' || operand.kind === 'or') {
    throw new SystemError({
      code: 'SEARCH_FILTER_UNSUPPORTED_NEGATED_GROUP',
      userMessage: 'Algolia filters do not support NOT applied to an ' + operand.kind.toUpperCase() + ' group.',
      retryable: false,
    });
  }

  throw new SystemError({
    code: 'SEARCH_FILTER_UNSUPPORTED_NEGATION',
    userMessage: 'Algolia filters can only negate an eq or eqStringLiteral predicate; received ' + operand.kind + '.',
    retryable: false,
  });
}

const atom = <T>(predicate: Extract<Predicate<T>, { kind: 'eq' | 'eqStringLiteral' | 'neq' }>): string =>
  field(predicate.field) +
  ':' +
  (predicate.kind === 'eqStringLiteral' ? quote(predicate.value) : scalar(predicate.value));

const comparison = <T>(predicate: Extract<Predicate<T>, { kind: 'cmp' | 'pathCmp' }>): string =>
  field(predicate.kind === 'pathCmp' ? predicate.path.join('.') : predicate.field) +
  ' ' +
  predicate.op +
  ' ' +
  finiteNumber(predicate.value);

interface AvailabilityWindow {
  readonly warehouseId: Scalar;
  readonly dayKey: string;
  readonly op: NumOp;
  readonly value: number;
}

const unsupportedElemMatch = (): never => {
  throw new SystemError({
    code: 'SEARCH_FILTER_UNSUPPORTED_ELEM_MATCH',
    userMessage:
      'Algolia filters only support elemMatch on availability as and(eq warehouseId, eq date, cmp available).',
    retryable: false,
  });
};

const availabilityWindow = <T>(predicate: Extract<Predicate<T>, { kind: 'elemMatch' }>): AvailabilityWindow => {
  if (predicate.field !== 'availability' || predicate.where.kind !== 'and') return unsupportedElemMatch();

  const clauses: readonly ElemPredicate[] = predicate.where.operands;
  const warehouse = clauses.find((clause) => clause.kind === 'eq' && clause.field === 'warehouseId');
  const date = clauses.find((clause) => clause.kind === 'eq' && clause.field === 'date');
  const available = clauses.find((clause) => clause.kind === 'cmp' && clause.field === 'available');

  if (clauses.length !== 3 || warehouse?.kind !== 'eq' || date?.kind !== 'eq' || available?.kind !== 'cmp') {
    return unsupportedElemMatch();
  }
  if (typeof date.value !== 'string') return unsupportedElemMatch();

  return { warehouseId: warehouse.value, dayKey: date.value, op: available.op, value: available.value };
};

const availabilityComparison = <T>(predicate: Extract<Predicate<T>, { kind: 'elemMatch' }>): string => {
  const window = availabilityWindow(predicate);

  return (
    field('inventory.' + String(window.warehouseId) + '.' + window.dayKey + 'T00:00:00.000Z.available') +
    ' ' +
    window.op +
    ' ' +
    finiteNumber(window.value)
  );
};

const negatedAtom = <T>(operand: Predicate<T>): string => {
  assertNegatable(operand);

  return 'NOT ' + atom(operand);
};

const validate = <T>(predicate: Predicate<T>, isInsideOr = false): void => {
  if (isInsideOr) assertNoGroupInsideOr(predicate);

  switch (predicate.kind) {
    case 'always':
    case 'never':
    case 'eqStringLiteral':
      return;
    case 'eq':
    case 'neq':
      scalar(predicate.value);
      return;
    case 'cmp':
    case 'pathCmp':
      finiteNumber(predicate.value);
      return;
    case 'in':
      assertNonEmptyInList(predicate);
      predicate.values.forEach((value) => scalar(value));
      return;
    case 'elemMatch':
      availabilityComparison(predicate);
      return;
    case 'not':
      validate(predicate.operand, isInsideOr);
      assertNegatable(predicate.operand);
      return;
    case 'and':
      predicate.operands.forEach((operand) => validate(operand, isInsideOr));
      return;
    case 'or':
      predicate.operands.forEach((operand) => validate(operand, true));
  }
};

const renderOr = <T>(operands: readonly Predicate<T>[]): string => {
  if (operands.some((operand) => operand.kind === 'always')) return '';

  const renderedOperands = operands
    .filter((operand) => operand.kind !== 'never')
    .map((operand) => render(operand))
    .filter((rendered) => rendered !== '');

  return renderedOperands.length === 0 ? GROUPED_NEVER_FILTER : '(' + renderedOperands.join(' OR ') + ')';
};

const renderAnd = <T>(operands: readonly Predicate<T>[]): string => {
  if (operands.some((operand) => operand.kind === 'never')) return NEVER_FILTER;

  return operands
    .map((operand) => render(operand))
    .filter((rendered) => rendered !== '')
    .join(' AND ');
};

const render = <T>(predicate: Predicate<T>): string => {
  switch (predicate.kind) {
    case 'always':
      return '';
    case 'never':
      return NEVER_FILTER;
    case 'eq':
    case 'eqStringLiteral':
      return atom(predicate);
    case 'neq':
      return 'NOT ' + atom(predicate);
    case 'cmp':
    case 'pathCmp':
      return comparison(predicate);
    case 'elemMatch':
      return availabilityComparison(predicate);
    case 'in':
      return '(' + predicate.values.map((value) => field(predicate.field) + ':' + scalar(value)).join(' OR ') + ')';
    case 'not':
      return negatedAtom(predicate.operand);
    case 'and':
      return renderAnd(predicate.operands);
    case 'or':
      return renderOr(predicate.operands);
  }
};

/**
 * Compiles a Predicate to Algolia `filters` syntax.
 * `always()` emits no constraint and `never()` emits a contradiction over
 * Algolia's always-filterable `_tags` attribute.
 */
export const toAlgoliaFilters = <T>(predicate: Predicate<T>): string => {
  validate(predicate);
  return render(predicate);
};

// A nil boost value renders as the tokens `null` / `undefined`, as the legacy template literal did.
export const toAlgoliaOptionalFilters = <T>(boosts: readonly Boost<T>[]): string[] =>
  boosts.map((boost) => boost.field + ':' + String(boost.value) + '<score=' + String(boost.weight) + '>');
